import React from 'react';
import { Link } from '@/components/LocaleLink';
import SEO from '@/components/SEO';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import useScrollReveal from '@/hooks/useScrollReveal';
import { trackEvent } from '@/utils/analytics';
import { useLanguage } from '@/contexts/language/LanguageContext';
import { digitalCfoCopy } from '@/contexts/language/translations/pages/digitalCfo';

/* ── Source systems we reconstruct the split from ── */
const SOURCE_ICONS = [
  { file: 'shopify_icon.png', alt: 'Shopify' },
  { file: 'shoptet_icon.png', alt: 'Shoptet' },
  { file: 'magento_icon.png', alt: 'Magento' },
  { file: 'ads_icon.png', alt: 'Google Ads' },
  { file: 'ga4_icon.png', alt: 'GA4' },
  { file: 'sap_icon.png', alt: 'SAP' },
  { file: 'abra_icon.png', alt: 'ABRA' },
  { file: 'bq_icon.png', alt: 'BigQuery' },
  { file: 'powerbi_icon.png', alt: 'Power BI' },
];

/* ── The layered build (copy in digitalCfo.ts) ── */
const PHASE_TAGS = ['v1', 'v2', 'v3', 'v4'];

const OurWorkDigitalCFO = () => {
  useScrollReveal();
  const { language } = useLanguage();
  const c = digitalCfoCopy[language];
  const PHASES = c.build.phases.map((phase, i) => ({ ...phase, tag: PHASE_TAGS[i] }));

  return (
    <div className="flex flex-col min-h-screen">
      <SEO
        title={c.seo.title}
        description={c.seo.description}
        canonical="/our-work/digital-cfo"
      />
      <Navigation />
      <main className="index-page flex-1">

        {/* Hero */}
        <section className="relative pt-28 pb-20 md:pt-36 md:pb-28 overflow-hidden bg-white">
          <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-[#FFF7EC] rounded-full filter blur-3xl opacity-60" />
          <div className="absolute bottom-20 right-0 w-[400px] h-[400px] bg-[#E0E7FF] rounded-full filter blur-3xl opacity-40" />

          <div className="container-custom relative z-10 reveal">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-[#2A1E47] mb-6 leading-[1.08]">
                {c.hero.title}
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-4">
                {c.hero.lead}
              </p>
              <p className="text-base text-muted-foreground/70 max-w-xl mx-auto mb-10">
                {c.hero.sub}
              </p>
              <div className={`flex flex-col sm:flex-row items-center justify-center gap-4${c.hero.ctaNote ? ' sm:items-stretch' : ''}`}>
                <Link
                  to="/tell-me-more"
                  className={`inline-flex items-center justify-center rounded-lg bg-[#FF6B35] text-white font-medium hover:bg-[#FF6B35]/90 transition-colors ${c.hero.ctaNote ? 'flex-col py-3 px-6 sm:px-8' : 'px-8 py-3'}`}
                >
                  {c.hero.ctaNote ? (
                    <>
                      <span className="text-lg sm:text-xl whitespace-nowrap">{c.hero.ctaPrimary}</span>
                      <span className="text-[10px] sm:text-[11px] font-normal opacity-80 whitespace-nowrap">{c.hero.ctaNote}</span>
                    </>
                  ) : c.hero.ctaPrimary}
                </Link>
                <button
                  onClick={() => {
                    trackEvent('digital_cfo_how_it_works', 'engagement', 'hero_cta_click');
                    document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  className="inline-flex items-center gap-2 px-8 py-3 rounded-lg border-2 border-[#2A1E47]/20 text-[#2A1E47] font-medium hover:border-[#2A1E47]/40 hover:bg-[#2A1E47]/5 transition-colors"
                >
                  {c.hero.ctaSecondary}
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Two worlds — dark provocation */}
        <section className="py-16 md:py-24 bg-[#2A1E47]">
          <div className="container-custom reveal">
            <div className="max-w-4xl mx-auto">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF6B35] mb-4">{c.twoWorlds.eyebrow}</p>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                {c.twoWorlds.title}
              </h2>
              <p className="text-lg text-white/60 mb-12 max-w-2xl">
                {c.twoWorlds.text}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="rounded-2xl p-7 bg-white/5 border border-white/10">
                  <h3 className="font-bold text-lg mb-1 text-white">{c.twoWorlds.statutoryTitle}</h3>
                  <p className="text-sm text-white/40 mb-6">{c.twoWorlds.statutorySub}</p>
                  <ul className="space-y-3 text-white/70 text-sm">
                    {c.twoWorlds.statutory.map((item, i) => (
                      <li key={i} className="flex gap-3">
                        <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full mt-2 bg-white/30" aria-hidden />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="rounded-2xl p-7 bg-[#FF6B35]/10 border border-[#FF6B35]/30">
                  <h3 className="font-bold text-lg mb-1 text-white">{c.twoWorlds.managementTitle} <span className="text-[#FF6B35]">{c.twoWorlds.managementTitleAccent}</span></h3>
                  <p className="text-sm text-white/40 mb-6">{c.twoWorlds.managementSub}</p>
                  <ul className="space-y-3 text-white/90 text-sm">
                    {c.twoWorlds.management.map((item, i) => (
                      <li key={i} className="flex gap-3">
                        <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full mt-2 bg-[#FF6B35]" aria-hidden />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* The contribution walk */}
        <section className="py-20 md:py-28 bg-white">
          <div className="container-custom reveal">
            <div className="max-w-4xl mx-auto text-center mb-14">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF6B35] mb-4">{c.model.eyebrow}</p>
              <h2 className="text-3xl md:text-4xl font-bold text-[#2A1E47] mb-4">
                {c.model.title}
              </h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                {c.model.text}
              </p>
            </div>

            {/* Walk steps */}
            <div className="max-w-4xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
              {c.model.steps.map((item, i) => (
                <div key={i} className="rounded-2xl border border-gray-100 bg-[#FAFAFA] p-6">
                  <span className="text-3xl font-bold text-[#2A1E47]/10 block mb-3">{`0${i + 1}`}</span>
                  <h3 className="font-bold text-[#2A1E47] mb-2 leading-snug">{item.step}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.detail}</p>
                </div>
              ))}
            </div>

            <p className="text-center text-sm text-muted-foreground/70 max-w-2xl mx-auto mb-6">
              {c.model.note}
            </p>
            <div className="text-center">
              <Link
                to="/our-work/retail-profit-management"
                onClick={() => trackEvent('digital_cfo_to_rpm', 'navigation', 'cross_link_click')}
                className="inline-flex items-center gap-2 text-[#FF6B35] font-medium hover:gap-3 transition-all"
              >
                {c.model.crossLink}
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </Link>
            </div>
          </div>
        </section>

        {/* The moat — rebuilt in data */}
        <section className="py-20 md:py-28 bg-[#FAFAFA]">
          <div className="container-custom reveal">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-center max-w-5xl mx-auto">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF6B35] mb-4">{c.moat.eyebrow}</p>
                <h2 className="text-3xl md:text-4xl font-bold text-[#2A1E47] mb-5">
                  {c.moat.title}
                </h2>
                <p className="text-muted-foreground mb-4 leading-relaxed">
                  {c.moat.p1Before}<span className="font-semibold text-[#2A1E47]">{c.moat.p1Highlight}</span>{c.moat.p1After}
                </p>
                <p className="text-muted-foreground mb-4 leading-relaxed">
                  {c.moat.p2}
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  {c.moat.p3}
                </p>
              </div>
              <div className="rounded-[24px] p-6 md:p-8 bg-white border border-gray-100 shadow-sm">
                <p className="text-sm font-semibold text-[#2A1E47]/50 uppercase tracking-wider mb-6 text-center">{c.moat.sourcesTitle}</p>
                <div className="grid grid-cols-3 gap-5 place-items-center">
                  {SOURCE_ICONS.map((logo) => (
                    <div key={logo.alt} className="rounded-full w-14 h-14 flex items-center justify-center bg-[#FAFAFA] border border-gray-100 shadow-sm flex-shrink-0 p-2.5">
                      <img
                        src={`/lovable-uploads/${logo.file}`}
                        alt={logo.alt}
                        className="w-full h-full object-contain"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Plan, forecast & AI */}
        <section className="py-20 md:py-28 bg-white">
          <div className="container-custom reveal">
            <div className="text-center max-w-2xl mx-auto mb-16">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF6B35] mb-4">{c.activate.eyebrow}</p>
              <h2 className="text-3xl md:text-4xl font-bold text-[#2A1E47] mb-4">
                {c.activate.title}
              </h2>
              <p className="text-muted-foreground text-lg">
                {c.activate.text}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
              {c.activate.cards.map((card) => (
                <div key={card.title} className="rounded-2xl border border-gray-100 bg-[#FAFAFA] p-7">
                  <h3 className="font-bold text-[#2A1E47] text-lg mb-3">{card.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{card.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How it works — layered build */}
        <section id="how-it-works" className="py-20 md:py-28 bg-[#FAFAFA]">
          <div className="container-custom reveal">
            <div className="max-w-4xl mx-auto">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF6B35] mb-4">{c.build.eyebrow}</p>
              <h2 className="text-3xl md:text-4xl font-bold text-[#2A1E47] mb-3">
                {c.build.title}
              </h2>
              <p className="text-muted-foreground text-lg mb-12 max-w-2xl">
                {c.build.text}
              </p>

              <div className="space-y-0">
                {PHASES.map((phase, i) => (
                  <div key={phase.tag} className="relative pl-12 md:pl-16 pb-12 last:pb-0">
                    {i < PHASES.length - 1 && (
                      <div className="absolute left-[19px] md:left-[23px] top-10 bottom-0 w-px bg-[#2A1E47]/10" />
                    )}
                    <div
                      className="absolute left-0 top-0 w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-md"
                      style={{ backgroundColor: i === PHASES.length - 1 ? '#FF6B35' : '#2A1E47' }}
                    >
                      {phase.tag}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-[#2A1E47] mb-2">{phase.title}</h3>
                      <p className="text-muted-foreground leading-relaxed">{phase.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA banner */}
        <section className="relative overflow-hidden py-20">
          <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B35] via-[#FF7F27] to-[#FF4500] rounded-3xl" />
          <div className="container-custom relative z-10">
            <div className="max-w-5xl mx-auto text-center pt-16 pb-8 px-4 reveal">
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black mb-10 text-white leading-tight whitespace-normal md:whitespace-nowrap">
                {c.cta.title}
              </h2>
              <p className="text-lg md:text-xl text-white/90 mb-10 max-w-xl mx-auto">
                {c.cta.text}
              </p>
              <div className="flex justify-center">
                <Link
                  to="/tell-me-more"
                  className="inline-flex items-center justify-center px-8 py-4 rounded-lg bg-[#2a1e48] hover:bg-[#2a1e48]/90 text-white text-base font-medium transition-all duration-300 shadow-lg border border-white/10"
                >
                  {c.cta.button}
                </Link>
              </div>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
};

export default OurWorkDigitalCFO;
